import React, { useState, useEffect, useRef } from 'react';
import { getFilteredNews, getAISummary } from '../../api/ai';
import { NiftyAISection } from '../ai/NiftyAISection';
import { MorningDigestCardBody } from './DashboardTab';
import { useSidePanel } from '../../context/SidePanelContext';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', {
      weekday:'short', day:'numeric', month:'short', year:'numeric',
    });
  } catch { return iso; }
}

function fmtIstTime(ts) {
  if (!ts) return null;
  try {
    const iso = ts.replace(' ', 'T') + (ts.includes('Z') ? '' : 'Z');
    return new Date(iso).toLocaleTimeString('en-IN', {
      timeZone:'Asia/Kolkata', hour:'2-digit', minute:'2-digit', hour12:false,
    }) + ' IST';
  } catch { return null; }
}

function isWeekend(dateIso) {
  const d = new Date(dateIso + 'T00:00:00');
  const dow = d.getDay();
  return dow === 0 || dow === 6;
}

// ── Market Analyst inline renderer ────────────────────────────────────────────

const YF_TOKEN_TO_KITE = {
  NSEI:'NIFTY 50',BSESN:'SENSEX',INDIAVIX:'INDIA VIX',NSEBANK:'NIFTY BANK',
  CNXIT:'NIFTY IT',CNXPHARMA:'NIFTY PHARMA',CNXFMCG:'NIFTY FMCG',CNXMETAL:'NIFTY METAL',
  CNXAUTO:'NIFTY AUTO',CNXREALTY:'NIFTY REALTY',CNXENERGY:'NIFTY ENERGY',
  CNXINFRA:'NIFTY INFRA',CNXPSUBANK:'NIFTY PSU BANK',CNXMEDIA:'NIFTY MEDIA',
  CNXCONSUM:'NIFTY CONSR DURBL',CNXCMDT:'NIFTY COMMODITIES',CNXPSE:'NIFTY PSE',
  CNXMNC:'NIFTY MNC',CNXSERVICE:'NIFTY SERV SECTOR',
};

const ANALYST_NON_TICKERS = new Set([
  'A','D','IST','UTC','NFP','GDP','RBI','FII','DII','PCR','VIX','OI','RS','RRG',
  'NSE','BSE','ETF','IPO','FPO','QoQ','YoY','MoM','CEO','CFO','MD','PB','PE',
  'EPS','ROE','EBITDA','CAGR','T','F','B','US','UK','EU','SEBI','NSDL',
  'CDSL','EMA','SMA','WMA','MA','NIFTY','NIFTY50','SENSEX','BANKNIFTY',
  'HIGH','MEDIUM','LOW','AND','OR','FOR','THE','NOT','BUT','ALL','NEW',
]);

const TICKER_RE = /\b([A-Z][A-Z0-9&]{1,14})\b/g;

function TickerText({ text }) {
  const { open: openPanel } = useSidePanel();
  if (!text) return null;
  const parts = [];
  let last = 0, m;
  TICKER_RE.lastIndex = 0;
  while ((m = TICKER_RE.exec(text)) !== null) {
    const token = m[1];
    if (ANALYST_NON_TICKERS.has(token) || token.length < 2) continue;
    const panelName = YF_TOKEN_TO_KITE[token] || token;
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(
      <span key={m.index}
        onClick={e => { e.stopPropagation(); openPanel({ name: panelName }); }}
        style={{ color:'var(--accent-blue)', fontWeight:600, cursor:'pointer',
          textDecoration:'underline dotted var(--accent-blue)', textUnderlineOffset:2 }}>
        {token}
      </span>
    );
    last = m.index + token.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

function MarketAnalystBody({ rj }) {
  if (!rj) return null;
  const SL = ({ children }) => (
    <div style={{ fontSize:10, fontWeight:700, letterSpacing:'.07em', textTransform:'uppercase',
      color:'var(--ink-3)', marginBottom:4, marginTop:8 }}>{children}</div>
  );
  const Bullets = ({ items }) => !items?.length ? null : (
    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
      {items.map((item, i) => (
        <div key={i} style={{ display:'flex', gap:6, alignItems:'flex-start' }}>
          <span style={{ fontSize:11, color:'var(--ink-3)', flexShrink:0, marginTop:1 }}>•</span>
          <span style={{ fontSize:11, color:'var(--ink)', lineHeight:1.55 }}><TickerText text={item}/></span>
        </div>
      ))}
    </div>
  );
  const Prose = ({ text }) => !text ? null : (
    <div style={{ fontSize:11, color:'var(--ink-2)', lineHeight:1.55 }}><TickerText text={text}/></div>
  );
  return (
    <div>
      {rj.whats_building?.length > 0 && <><SL>What's Building</SL><Bullets items={rj.whats_building}/></>}
      {rj.whats_turning?.length  > 0 && <><SL>What's Turning</SL><Bullets items={rj.whats_turning}/></>}
      {rj.index_structure        &&     <><SL>Index Structure</SL><Prose text={rj.index_structure}/></>}
      {rj.rotation               &&     <><SL>Rotation</SL><Prose text={rj.rotation}/></>}
      {rj.breadth_note           &&     <><SL>Breadth</SL><Prose text={rj.breadth_note}/></>}
      {rj.on_the_radar?.length   > 0 && <><SL>On the Radar</SL><Bullets items={rj.on_the_radar}/></>}
    </div>
  );
}

// ── Sub-section row inside a day card ─────────────────────────────────────────

function SubSection({ label, genTime, children, missing }) {
  const [open, setOpen] = useState(true);  // always open by default

  return (
    <div style={{ borderTop:'.5px solid var(--hair)', paddingTop:2 }}>
      <div onClick={() => !missing && setOpen(o => !o)} style={{
        display:'flex', alignItems:'center', padding:'7px 0 5px',
        cursor: missing ? 'default' : 'pointer', userSelect:'none', gap:8,
      }}>
        <span style={{ fontSize:10, fontWeight:700, letterSpacing:'.07em',
          textTransform:'uppercase', color: missing ? 'var(--ink-4)' : 'var(--ink-3)' }}>
          {label}
        </span>
        {genTime && <span style={{ fontSize:10, color:'var(--ink-4)' }}>{genTime}</span>}
        <div style={{ flex:1 }}/>
        {missing
          ? <span style={{ fontSize:10, color:'var(--ink-4)', fontStyle:'italic' }}>not yet generated</span>
          : <span style={{ fontSize:10, color:'var(--ink-4)',
              transform: open ? 'rotate(180deg)' : 'none', transition:'transform .15s' }}>▼</span>}
      </div>
      {!missing && open && (
        <div style={{ paddingBottom:10 }}>{children}</div>
      )}
    </div>
  );
}

// ── Single digest day card ────────────────────────────────────────────────────

function DigestDayCard({ day, isToday, defaultExpanded }) {
  const [open, setOpen] = useState(defaultExpanded);
  const meta = day.meta || {};

  const hasMorning = !!day.dashboardRaw;
  const hasNifty   = !!day.marketInterp;
  const hasAnalyst = !!day.marketAnalyst;

  const headline = day.dashboardRaw?.rationale || day.headline || '';

  return (
    <div className="card" style={{ marginBottom:10, overflow:'hidden' }}>
      {/* Card header */}
      <div onClick={() => setOpen(o => !o)} style={{
        display:'flex', alignItems:'center', gap:10,
        padding:'9px 14px', background:'var(--surface-2)',
        cursor:'pointer', userSelect:'none',
      }}>
        <span style={{ fontSize:12, fontWeight:700, color:'var(--ink)', flexShrink:0 }}>
          {fmtDate(day.date)}
        </span>
        {isToday && (
          <span style={{ fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:3,
            background:'var(--accent-blue)', color:'#fff', letterSpacing:'.04em' }}>
            TODAY
          </span>
        )}
        {/* Collapsed: show headline snippet */}
        {!open && headline && (
          <span style={{ fontSize:11, color:'var(--ink-3)', overflow:'hidden',
            textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1, minWidth:0 }}>
            {headline.length > 120 ? headline.slice(0, 120) + '…' : headline}
          </span>
        )}
        {/* Expanded: show available-report badges */}
        {open && (
          <div style={{ display:'flex', gap:4, flex:1 }}>
            {hasMorning && <span style={{ fontSize:9, padding:'1px 5px', borderRadius:3,
              background:'var(--surface-3)', color:'var(--ink-3)', fontWeight:600 }}>Morning</span>}
            {hasNifty   && <span style={{ fontSize:9, padding:'1px 5px', borderRadius:3,
              background:'var(--surface-3)', color:'var(--ink-3)', fontWeight:600 }}>NIFTY</span>}
            {hasAnalyst && <span style={{ fontSize:9, padding:'1px 5px', borderRadius:3,
              background:'var(--surface-3)', color:'var(--ink-3)', fontWeight:600 }}>Market AI</span>}
          </div>
        )}
        {!open && !headline && <div style={{ flex:1 }}/>}
        <span style={{ fontSize:11, color:'var(--ink-3)', flexShrink:0,
          transform: open ? 'rotate(180deg)' : 'none', transition:'transform .2s' }}>▼</span>
      </div>

      {open && (
        <div style={{ padding:'4px 14px 6px' }}>

          <SubSection label="Morning Digest" genTime={fmtIstTime(meta.dashboard)} missing={!hasMorning}>
            <MorningDigestCardBody digest={day.dashboardRaw}/>
          </SubSection>

          <SubSection label="NIFTY Daily Prediction" genTime={fmtIstTime(meta.nifty)} missing={!hasNifty}>
            {hasNifty && (
              <NiftyAISection
                ai={day.marketInterp}
                niftyData={null}
                livePrice={null}
                diagnostics={null}
                inputSnapshot={day.marketInterp._input_snapshot || null}
                marketSnapshot={day.marketInterp.market_snapshot || null}
              />
            )}
          </SubSection>

          <SubSection label="Market AI" genTime={fmtIstTime(meta.market_analyst)} missing={!hasAnalyst}>
            {hasAnalyst && <MarketAnalystBody rj={day.marketAnalyst}/>}
          </SubSection>

          {/* Coming soon */}
          <div style={{ borderTop:'.5px solid var(--hair)', padding:'7px 0' }}>
            <span style={{ fontSize:10, fontWeight:700, letterSpacing:'.07em', textTransform:'uppercase',
              color:'var(--ink-4)' }}>AI Strategies</span>
            <span style={{ fontSize:10, color:'var(--ink-4)', fontStyle:'italic', marginLeft:8 }}>coming soon</span>
          </div>
          <div style={{ borderTop:'.5px solid var(--hair)', padding:'7px 0' }}>
            <span style={{ fontSize:10, fontWeight:700, letterSpacing:'.07em', textTransform:'uppercase',
              color:'var(--ink-4)' }}>AI Trade Journal</span>
            <span style={{ fontSize:10, color:'var(--ink-4)', fontStyle:'italic', marginLeft:8 }}>coming soon</span>
          </div>

        </div>
      )}
    </div>
  );
}

// ── Section labels ────────────────────────────────────────────────────────────

const SL = ({ children }) => (
  <div style={{ fontSize:13, fontWeight:700, letterSpacing:'.06em', textTransform:'uppercase',
    color:'var(--ink)', margin:'20px 0 12px', paddingBottom:6,
    borderBottom:'.5px solid var(--hair-strong)' }}>
    {children}
  </div>
);

function CollapsibleSL({ children, open, onToggle }) {
  return (
    <div onClick={onToggle} style={{
      display:'flex', alignItems:'center', justifyContent:'space-between',
      fontSize:13, fontWeight:700, letterSpacing:'.06em', textTransform:'uppercase',
      color:'var(--ink)', margin:'20px 0 12px', paddingBottom:6,
      borderBottom:'.5px solid var(--hair-strong)',
      cursor:'pointer', userSelect:'none',
    }}>
      <span>{children}</span>
      <span style={{ fontSize:11, color:'var(--ink-3)',
        transform: open ? 'rotate(180deg)' : 'none', transition:'transform .2s' }}>▼</span>
    </div>
  );
}

// ── News archive ──────────────────────────────────────────────────────────────

const NEWS_CAT_STYLE = {
  'Indian Equity':        { bg:'#daeeff', c:'#1060a0' },
  'Macro':                { bg:'#eeedfe', c:'#3838a0' },
  'Global Markets':       { bg:'#eaf4ea', c:'#1a6a1a' },
  'Natural Gas & Energy': { bg:'#faecd8', c:'#7a3e00' },
  'FII Flows':            { bg:'#d8f4ec', c:'#0a5a40' },
  'Geopolitical':         { bg:'#fce8e8', c:'#7a1a1a' },
  'NSE/BSE Circulars':    { bg:'#f0f0f0', c:'#444'    },
};

const NEWS_CATS = ['Indian Equity','Global Markets','Macro','FII Flows',
  'Natural Gas & Energy','Geopolitical','NSE/BSE Circulars'];

function timeAgo(published) {
  if (!published) return '';
  const diff = Date.now() - new Date(published).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function NewsItem({ item }) {
  const cs = NEWS_CAT_STYLE[item.category] || { bg:'var(--surface-3)', c:'var(--ink-3)' };
  return (
    <div style={{ padding:'9px 0', borderBottom:'.5px solid var(--hair)' }}>
      <div style={{ marginBottom:4 }}>
        <a href={item.url} target="_blank" rel="noopener noreferrer"
          style={{ fontSize:12, fontWeight:600, color:'var(--ink)', lineHeight:1.45, textDecoration:'none' }}
          onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
          onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>
          {item.title}
        </a>
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:5, flexWrap:'wrap' }}>
        <span style={{ fontSize:11, color:'var(--ink-3)' }}>{item.source}</span>
        <span style={{ fontSize:11, color:'var(--ink-3)' }}>·</span>
        <span style={{ fontSize:11, color:'var(--ink-3)' }}>{timeAgo(item.published || item.archived_at)}</span>
        {item.category && <>
          <span style={{ fontSize:11, color:'var(--ink-3)' }}>·</span>
          <span style={{ fontSize:10, fontWeight:600, padding:'1px 6px', borderRadius:3,
            background:cs.bg, color:cs.c }}>{item.category}</span>
        </>}
        {(item.tickers||[]).map(t => (
          <span key={t} style={{ fontSize:10, fontWeight:600, padding:'1px 5px', borderRadius:3,
            background:'var(--surface-2)', border:'.5px solid var(--hair)', color:'var(--ink-2)' }}>{t}</span>
        ))}
      </div>
    </div>
  );
}

function NewsArchive() {
  const [allItems,   setAllItems]   = useState([]);
  const [status,     setStatus]     = useState('loading');
  const [query,      setQuery]      = useState('');
  const [activeCats, setActiveCats] = useState(new Set());
  const searchRef = useRef(null);

  useEffect(() => {
    getFilteredNews({ hours:48, limit:200 })
      .then(res => { setAllItems(res?.items || []); setStatus('ok'); })
      .catch(() => setStatus('error'));
  }, []);

  const toggleCat = cat => setActiveCats(prev => {
    const next = new Set(prev); next.has(cat) ? next.delete(cat) : next.add(cat); return next;
  });

  const q = query.trim().toLowerCase();
  const visible = allItems.filter(it => {
    if (activeCats.size > 0 && !activeCats.has(it.category)) return false;
    if (!q) return true;
    return it.title?.toLowerCase().includes(q) || it.source?.toLowerCase().includes(q) ||
      (it.tickers||[]).some(t => t.toLowerCase().includes(q)) || it.category?.toLowerCase().includes(q);
  });

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:10, flexWrap:'wrap' }}>
        <div style={{ display:'flex', gap:4, flexWrap:'wrap', flex:1 }}>
          {NEWS_CATS.map(cat => {
            const cs = NEWS_CAT_STYLE[cat]||{bg:'var(--surface-3)',c:'var(--ink-3)'};
            const on = activeCats.has(cat);
            return (
              <button key={cat} onClick={() => toggleCat(cat)} style={{
                fontSize:10, fontWeight:600, padding:'3px 8px', borderRadius:3,
                border: on ? 'none' : '.5px solid var(--hair)',
                background: on ? cs.bg : 'var(--surface-2)', color: on ? cs.c : 'var(--ink-3)',
                cursor:'pointer', whiteSpace:'nowrap' }}>{cat}</button>
            );
          })}
          {activeCats.size > 0 && (
            <button onClick={() => setActiveCats(new Set())} style={{
              fontSize:10, padding:'3px 8px', borderRadius:3, border:'.5px solid var(--hair)',
              background:'none', color:'var(--ink-3)', cursor:'pointer' }}>Clear</button>
          )}
        </div>
        <div style={{ position:'relative', flexShrink:0 }}>
          <input ref={searchRef} type="text" value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Search…" style={{
              width:130, padding:'4px 24px 4px 8px', fontSize:11, fontFamily:'var(--font-ui)',
              background:'var(--surface-2)', color:'var(--ink)',
              border:'.5px solid var(--hair)', borderRadius:5, outline:'none' }}/>
          {query && (
            <button onClick={() => { setQuery(''); searchRef.current?.focus(); }} style={{
              position:'absolute', right:6, top:'50%', transform:'translateY(-50%)',
              background:'none', border:'none', cursor:'pointer', fontSize:13,
              color:'var(--ink-3)', padding:0, lineHeight:1 }}>×</button>
          )}
        </div>
      </div>
      {status==='loading' && <div style={{ padding:'24px', textAlign:'center', color:'var(--ink-3)', fontSize:11 }}>Loading…</div>}
      {status==='error'   && <div style={{ padding:'24px', textAlign:'center', color:'var(--ink-3)', fontSize:11 }}>Could not load archive.</div>}
      {status==='ok' && visible.length === 0 && (
        <div style={{ padding:'24px', textAlign:'center', color:'var(--ink-3)', fontSize:11 }}>
          {q||activeCats.size>0 ? 'No results.' : 'No news in the last 48h.'}
        </div>
      )}
      {status==='ok' && visible.length > 0 && (
        <div>
          {visible.map(item => <NewsItem key={item.item_id} item={item}/>)}
          <div style={{ paddingTop:8, fontSize:11, color:'var(--ink-3)', textAlign:'right' }}>
            {visible.length}{(q||activeCats.size>0)?` of ${allItems.length}`:''} item{visible.length!==1?'s':''} · last 48h
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function AISummaryTab() {
  const [days,     setDays]     = useState([]);
  const [status,   setStatus]   = useState('loading');
  const [newsOpen, setNewsOpen] = useState(false);

  useEffect(() => {
    getAISummary(14)
      .then(res => {
        // Filter out weekends — market digests only exist for trading days
        const weekdays = (res?.days || []).filter(d => !isWeekend(d.date));
        setDays(weekdays);
        setStatus('ok');
      })
      .catch(() => setStatus('error'));
  }, []);

  const todayIso = new Date().toLocaleDateString('sv');

  return (
    <div style={{ maxWidth:1200, margin:'0 auto' }}>

      <SL>Digest</SL>

      {status === 'loading' && (
        <div style={{ padding:'32px', textAlign:'center', color:'var(--ink-3)', fontSize:11 }}>
          Loading digest…
        </div>
      )}
      {status === 'error' && (
        <div style={{ padding:'32px', textAlign:'center', color:'var(--ink-3)', fontSize:11 }}>
          Could not load digest.
        </div>
      )}
      {status === 'ok' && days.length === 0 && (
        <div style={{ padding:'32px', textAlign:'center', color:'var(--ink-3)', fontSize:11 }}>
          No reports yet — generate them from the individual tabs.
        </div>
      )}
      {status === 'ok' && days.map((day, i) => (
        <DigestDayCard
          key={day.date}
          day={day}
          isToday={day.date === todayIso}
          defaultExpanded={i === 0}
        />
      ))}

      <CollapsibleSL open={newsOpen} onToggle={() => setNewsOpen(o => !o)}>
        News
      </CollapsibleSL>
      {newsOpen && (
        <div style={{ height:520, overflowY:'auto', paddingRight:4 }}>
          <NewsArchive/>
        </div>
      )}

    </div>
  );
}
